import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useTheme, ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Contextos
import { AuthProvider } from './context/AuthContext';
import { BrandProvider } from './context/BrandContext';
import { ViewSettingsProvider } from './context/ViewSettingsContext';
import { TagSettingsProvider } from './context/TagSettingsContext';
import { MIAProvider } from './context/MIAContext';
import { SystemFeaturesProvider } from './context/SystemFeaturesContext';
import { ThemeProvider } from './design-system/providers/ThemeProvider';
import PlanLimitDialog from './components/molecules/PlanLimitDialog/PlanLimitDialog';

// React Query
import { queryClient } from './config/queryClient';

// Componentes de Rota
import PrivateRoute from './components/organisms/PrivateRoute';

// Rotas Modularizadas
import { PublicRoutes, PrivateRoutes, AdminRoutes } from './routes';

// Páginas Públicas
import LoginPage from './pages/auth/LoginPage';
import Register from './pages/Register';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import PublicArticlePage from './pages/PublicArticlePage';
import PublicPrintMediaPage from './pages/PublicPrintMediaPage';
import TestPublicPage from './pages/TestPublicPage';
import ActivationPage from './pages/auth/ActivationPage';
import OnboardingPage from './pages/auth/OnboardingPage';
import PublicDashboardPage from './pages/public/PublicDashboardPage';

/**
 * AdminRoutesWrapper - Wrapper para rotas admin com ThemeProvider customizado
 */
function AdminRoutesWrapper() {
  return (
    <BrandProvider>
      <MIAProvider>
        <ViewSettingsProvider>
          <TagSettingsProvider>
            <ThemeProvider>
              <CssVarUpdater />
              <Routes>
                {AdminRoutes()}
              </Routes>
            </ThemeProvider>
          </TagSettingsProvider>
        </ViewSettingsProvider>
      </MIAProvider>
    </BrandProvider>
  );
}

/**
 * CssVarUpdater - Atualiza variáveis CSS quando tema muda
 */
function CssVarUpdater() {
  const theme = useTheme();
  React.useEffect(() => {
    document.documentElement.style.setProperty('--mui-primary-main', theme.palette.primary.main);
  }, [theme.palette.primary.main]);
  return null;
}

/**
 * App - Componente raiz da aplicação
 */
function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const showReactQueryDevtools =
    process.env.NODE_ENV === 'development' &&
    process.env.REACT_APP_QUERY_DEVTOOLS === 'true';

  return (
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={googleClientId || ''}>
        <Router>
          <AuthProvider>
            <SystemFeaturesProvider>
            <BrandProvider>
              <MIAProvider>
                <Routes>
                  {/* Rotas Públicas - com ThemeProvider customizado */}
                  <Route
                    path="/login"
                    element={
                      <ThemeProvider>
                        <CssVarUpdater />
                        <LoginPage />
                      </ThemeProvider>
                    }
                  />
                  <Route
                    path="/register"
                    element={
                      <ThemeProvider>
                        <CssVarUpdater />
                        <Register />
                      </ThemeProvider>
                    }
                  />
                  <Route
                    path="/forgot-password"
                    element={
                      <ThemeProvider>
                        <CssVarUpdater />
                        <ForgotPasswordPage />
                      </ThemeProvider>
                    }
                  />
                  <Route
                    path="/reset-password/:token"
                    element={
                      <ThemeProvider>
                        <CssVarUpdater />
                        <ResetPasswordPage />
                      </ThemeProvider>
                    }
                  />
                  <Route
                    path="/article/:articleId"
                    element={
                      <ThemeProvider>
                        <CssVarUpdater />
                        <PublicArticlePage />
                      </ThemeProvider>
                    }
                  />
                  <Route
                    path="/print/:articleId"
                    element={
                      <ThemeProvider>
                        <CssVarUpdater />
                        <PublicPrintMediaPage />
                      </ThemeProvider>
                    }
                  />
                  <Route
                    path="/test/:articleId"
                    element={
                      <ThemeProvider>
                        <CssVarUpdater />
                        <TestPublicPage />
                      </ThemeProvider>
                    }
                  />

                  {/* Dashboard Público Compartilhado */}
                  <Route
                    path="/dashboard/:slug/:token"
                    element={
                      <ThemeProvider>
                        <CssVarUpdater />
                        <PublicDashboardPage />
                      </ThemeProvider>
                    }
                  />
                  <Route
                    path="/dashboard/:token"
                    element={
                      <ThemeProvider>
                        <CssVarUpdater />
                        <PublicDashboardPage />
                      </ThemeProvider>
                    }
                  />
                  <Route
                    path="/dashboard/*"
                    element={
                      <ThemeProvider>
                        <CssVarUpdater />
                        <PublicDashboardPage />
                      </ThemeProvider>
                    }
                  />
                  <Route
                    path="/d/:slug/:token"
                    element={
                      <ThemeProvider>
                        <CssVarUpdater />
                        <PublicDashboardPage />
                      </ThemeProvider>
                    }
                  />
                  <Route
                    path="/d/:token"
                    element={
                      <ThemeProvider>
                        <CssVarUpdater />
                        <PublicDashboardPage />
                      </ThemeProvider>
                    }
                  />
                  <Route
                    path="/public/dashboard/:token"
                    element={
                      <ThemeProvider>
                        <CssVarUpdater />
                        <PublicDashboardPage />
                      </ThemeProvider>
                    }
                  />

                  {/* Ativação da conta e Onboarding (semi-públicas: precisam de auth mas não de ativação) */}
                  <Route
                    path="/activate"
                    element={
                      <ThemeProvider>
                        <CssVarUpdater />
                        <ActivationPage />
                      </ThemeProvider>
                    }
                  />
                  <Route
                    path="/onboarding"
                    element={
                      <ThemeProvider>
                        <CssVarUpdater />
                        <OnboardingPage />
                      </ThemeProvider>
                    }
                  />

                  {/* Rotas Administrativas - MUI puro com tema simples light/dark */}
                  <Route path="/admin/*" element={<AdminRoutesWrapper />} />

                  {/* Rotas do Cliente (Privadas) - com ThemeProvider customizado */}
                  <Route
                    path="/*"
                    element={
                      <PrivateRoute>
                        <ViewSettingsProvider>
                          <TagSettingsProvider>
                            <ThemeProvider>
                              <CssVarUpdater />
                              <PrivateRoutes />
                            </ThemeProvider>
                          </TagSettingsProvider>
                        </ViewSettingsProvider>
                      </PrivateRoute>
                    }
                  />
                </Routes>
              </MIAProvider>
            </BrandProvider>
            {/* Dialog global de upsell quando limite mensal é atingido */}
            <ThemeProvider>
              <CssVarUpdater />
              <PlanLimitDialog />
            </ThemeProvider>
            </SystemFeaturesProvider>
          </AuthProvider>
        </Router>
      </GoogleOAuthProvider>

      {/* React Query Devtools - habilite com REACT_APP_QUERY_DEVTOOLS=true */}
      {showReactQueryDevtools && (
        <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
      )}
    </QueryClientProvider>
  );
}

export default App;
